import { useState, useCallback } from 'react';

interface QuestionOption {
  id: string;
  value: string;
  wizardOnly?: boolean;
  tooltip?: string;
  conditional?: OptionConditionalBehavior;
}

interface UseOptionsEditorParams {
  options: QuestionOption[];
  onChange: (options: QuestionOption[]) => void;
}

export function useOptionsEditor({ options, onChange }: UseOptionsEditorParams) {
  const [expandedOption, setExpandedOption] = useState<string | null>(null);
  const [conditionalOption, setConditionalOption] = useState<string | null>(null);
  const [newOption, setNewOption] = useState({ id: '', value: '' });

  const handleAddOption = useCallback(() => {
    if (!newOption.id.trim() || !newOption.value.trim()) return;
    onChange([...options, { id: newOption.id.trim(), value: newOption.value.trim() }]);
    setNewOption({ id: '', value: '' });
  }, [newOption, options, onChange]);

  const handleUpdateOption = useCallback(
    (index: number, updates: Partial<QuestionOption>) => {
      const updated = [...options];
      updated[index] = { ...updated[index], ...updates };
      onChange(updated);
    },
    [options, onChange],
  );

  const handleRemoveOption = useCallback(
    (index: number) => {
      onChange(options.filter((_, optionIndex) => optionIndex !== index));
    },
    [options, onChange],
  );

  const handleMoveOption = useCallback(
    (index: number, direction: 'up' | 'down') => {
      if (
        (direction === 'up' && index === 0) ||
        (direction === 'down' && index === options.length - 1)
      ) {
        return;
      }
      const updated = [...options];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
      onChange(updated);
    },
    [options, onChange],
  );

  const toggleExpandedOption = useCallback(
    (optionId: string) => {
      setExpandedOption(expandedOption === optionId ? null : optionId);
    },
    [expandedOption],
  );

  const openConditionalOption = useCallback((optionId: string) => {
    setConditionalOption(optionId);
  }, []);

  const closeConditionalOption = useCallback(() => {
    setConditionalOption(null);
  }, []);

  const handleNewOptionIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewOption(prev => ({ ...prev, id: event.target.value }));
    },
    [],
  );

  const handleNewOptionValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewOption(prev => ({ ...prev, value: event.target.value }));
    },
    [],
  );

  return {
    expandedOption,
    conditionalOption,
    newOption,
    handleAddOption,
    handleUpdateOption,
    handleRemoveOption,
    handleMoveOption,
    toggleExpandedOption,
    openConditionalOption,
    closeConditionalOption,
    handleNewOptionIdChange,
    handleNewOptionValueChange,
  };
}
